// src/components/navigation/Atelier4Navigation.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Target,
  Database,
  Brain
} from 'lucide-react';
import { getUnsavedChanges, setNavigationWarningDialogOpen, getTabProgressionStatus } from '../../utils/navigationUtils';

// Define tab keys for Workshop 4 phases
const ATELIER4_TABS = {
  THREAT_INTELLIGENCE: 'atelier4-threat-intelligence',
  CTI_SCENARIOS: 'atelier4-cti-scenarios'
};

const Atelier4Navigation = ({
  activeTab,
  setActiveTab,
  expanded,
  toggleExpand,
  baseNavItemClass,
  workshopTitleClass,
  activeStyle,
  inactiveStyle,
  completedTabs,
  progress = 0
}) => {
  const { t } = useTranslation();

  // Check if any tab within this workshop is active
  const isAtelierActive = Object.values(ATELIER4_TABS).includes(activeTab);

  // Sub-item class with added padding
  const subItemClass = `${baseNavItemClass} pl-8`; // Indent sub-items

  const renderProgressionDot = (tabId) => {
    const status = getTabProgressionStatus(tabId, completedTabs);
    let colorClass = 'bg-slate-600'; // pending
    if (status === 'completed') {
      colorClass = 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]';
    } else if (status === 'next') {
      colorClass = 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)] animate-pulse';
    }
    return <div className={`w-2 h-2 rounded-full flex-shrink-0 ml-auto ${colorClass}`} title={status === 'completed' ? 'Complété' : status === 'next' ? 'En cours' : 'À venir'} />
  };

  // Generic handler for tab navigation with unsaved changes check
  const handleTabClick = async (tabKey) => {
    // Check for unsaved changes before navigation
    if (getUnsavedChanges()) {
      // Show the warning dialog
      setNavigationWarningDialogOpen(true);
      // Store the target tab for later use
      localStorage.setItem('ebiosrm_pending_tab', tabKey);
      return;
    }
    // If no unsaved changes, navigate directly
    setActiveTab(tabKey);
  };

  return (
    <li className="w-full">
      <button
        className={`${workshopTitleClass} ${isAtelierActive ? 'text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'} group`}
        onClick={toggleExpand}
      >
        <div className="flex items-center w-full justify-between">
          <div className="flex items-center">
            <div className={`${isAtelierActive ? 'bg-blue-500' : 'bg-gray-700 group-hover:bg-gray-600'} p-1.5 rounded-md transition-colors duration-200 mr-3`}>
              <Target size={16} className="flex-shrink-0 text-white" />
            </div>
            <span className="truncate">{t('workshop4.navigationTitle')}</span>
            {progress >= 0 && (
              <div className="ml-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-900/40 border border-white/5 shadow-inner backdrop-blur-md transition-all duration-300 group-hover:bg-slate-800/60">
                <svg className="w-3.5 h-3.5 -rotate-90" viewBox="0 0 36 36">
                  <path className="text-slate-700/60" strokeWidth="3.5" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className={`transition-all duration-1000 ease-out ${progress === 100 ? 'text-emerald-400 drop-shadow-[0_0_3px_rgba(52,211,153,0.5)]' : 'text-blue-400 drop-shadow-[0_0_3px_rgba(96,165,250,0.5)]'}`} strokeWidth="3.5" strokeDasharray={`${progress}, 100`} stroke="currentColor" fill="none" strokeLinecap="round" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span className={`text-[10px] font-bold tracking-wider ${progress === 100 ? 'text-emerald-400' : 'text-blue-400'}`}>
                  {progress}%
                </span>
              </div>
            )}
          </div>
          <div className="text-gray-400 transition-transform duration-200">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </div>
      </button>

      {expanded && (
        <ul className="mt-1 space-y-1 w-full">
          {/* Phase 1: Threat Intelligence */}
          <li>
            <button
              className={`${subItemClass} ${activeTab === ATELIER4_TABS.THREAT_INTELLIGENCE ? activeStyle : inactiveStyle} group`}
              onClick={() => handleTabClick(ATELIER4_TABS.THREAT_INTELLIGENCE)}
            >
              <div className="flex items-center w-full">
                <div className={`${activeTab === ATELIER4_TABS.THREAT_INTELLIGENCE ? 'bg-blue-500' : 'bg-gray-700 group-hover:bg-gray-600'} p-1 rounded transition-colors duration-200 mr-2`}>
                  <Database size={14} className="flex-shrink-0 text-white" />
                </div>
                <span className="truncate">{t('workshop4.navigation.threatIntelligence')}</span>
                {renderProgressionDot(ATELIER4_TABS.THREAT_INTELLIGENCE)}
              </div>
            </button>
          </li>

          {/* Phase 2: CTI-based Operational Scenarios */}
          <li>
            <button
              className={`${subItemClass} ${activeTab === ATELIER4_TABS.CTI_SCENARIOS ? activeStyle : inactiveStyle} group`}
              onClick={() => handleTabClick(ATELIER4_TABS.CTI_SCENARIOS)}
            >
              <div className="flex items-center w-full">
                <div className={`${activeTab === ATELIER4_TABS.CTI_SCENARIOS ? 'bg-blue-500' : 'bg-gray-700 group-hover:bg-gray-600'} p-1 rounded transition-colors duration-200 mr-2`}>
                  <Brain size={14} className="flex-shrink-0 text-white" />
                </div>
                <span className="truncate">{t('workshop4.navigation.operationalScenarios')}</span>
                {renderProgressionDot(ATELIER4_TABS.CTI_SCENARIOS)}
              </div>
            </button>
          </li>


        </ul>
      )}
    </li>
  );
};

export default Atelier4Navigation;
export { ATELIER4_TABS };